import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

interface DeleteConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  isDeleting?: boolean;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title = "Delete Index",
  message = "Are you sure you want to delete this index? This action cannot be undone.",
  isDeleting = false
}) => {
  if (!open) return null;

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md mx-4 glass-card rounded-2xl shadow-2xl border border-border/50 bg-background/90 backdrop-blur-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
                             <div className="relative">
                 <div className="absolute inset-0 bg-cyber-orange/20 rounded-full blur-md"></div>
                 <div className="relative bg-cyber-orange/10 p-2 rounded-full border border-cyber-orange/30">
                   <AlertTriangle className="w-5 h-5 text-cyber-orange" />
                 </div>
               </div>
              <h2 className="text-xl font-bold text-foreground">
                {title}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            >
              <X size={16} />
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {message}
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="flex-1 border border-border/50 hover:bg-background/50"
                disabled={isDeleting}
              >
                Cancel
              </Button>
                             <Button
                 onClick={handleConfirm}
                 disabled={isDeleting}
                 className="flex-1 bg-gradient-to-r from-cyber-orange to-cyber-orange/80 hover:from-cyber-orange/90 hover:to-cyber-orange/70 text-white font-semibold shadow-lg"
               >
                {isDeleting ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Deleting...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </span>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}; 